import { SUPPORT_SERVICE_URL } from '@/config'
import type {
  LinkPreviewDto,
  TicketDto,
  TicketMessageDto,
} from '@/modules/support/types'
import { getAuthorizationHeader } from '@/services/lukita/utils'

export class SupportApiError extends Error {
  status: number

  constructor(message: string, status: number) {
    super(message)
    this.name = 'SupportApiError'
    this.status = status
  }
}

const ERROR_COPY: Record<string, string> = {
  SUBJECT_AND_DESCRIPTION_REQUIRED: 'Add a subject and a description.',
  BODY_REQUIRED: 'Write a message before sending.',
  TEXT_TOO_LONG: 'Your message is too long. Please shorten it.',
  PAYLOAD_TOO_LARGE: 'Your attachments are too large to send.',
  RATE_LIMITED: 'Too many requests. Wait a moment and try again.',
  NOT_FOUND: 'This ticket could not be found.',
  UNAUTHORIZED: 'Your session expired. Sign in again.',
  NO_ACCOUNT: 'Your account could not be found.',
}

const readErrorMessage = async (response: Response): Promise<string> => {
  try {
    const payload = await response.json()
    const raw = payload.message ?? payload.error
    if (typeof raw === 'string') return ERROR_COPY[raw] ?? raw
    return `Support request failed (${response.status})`
  } catch {
    return `Support request failed (${response.status})`
  }
}

const request = async (
  route: string,
  init: RequestInit = {},
): Promise<Response> => {
  const response = await fetch(`${SUPPORT_SERVICE_URL}${route}`, {
    ...init,
    headers: { ...(await getAuthorizationHeader()), ...init.headers },
  })

  if (!response.ok) {
    throw new SupportApiError(await readErrorMessage(response), response.status)
  }

  return response
}

export const getTickets = async (): Promise<{ tickets: TicketDto[] }> =>
  (await request('/tickets')).json()

export const getTicket = async (
  publicId: string,
): Promise<{ ticket: TicketDto; messages: TicketMessageDto[] }> =>
  (await request(`/tickets/${publicId}`)).json()

export const postTicket = async (
  form: FormData,
): Promise<{ ticket: TicketDto }> =>
  (await request('/tickets', { method: 'POST', body: form })).json()

export const postTicketMessage = async (
  publicId: string,
  form: FormData,
): Promise<{ ticket: TicketDto; message: TicketMessageDto }> =>
  (
    await request(`/tickets/${publicId}/messages`, {
      method: 'POST',
      body: form,
    })
  ).json()

// Presence calls answer 204, so there is never a body to read. The service caps
// them tightly (typing 40/min, read 20/min); callers must swallow the 429.
export const postTicketTyping = async (publicId: string): Promise<void> => {
  await request(`/tickets/${publicId}/typing`, { method: 'POST' })
}

export const postTicketRead = async (publicId: string): Promise<void> => {
  await request(`/tickets/${publicId}/read`, { method: 'POST' })
}

// Answers null when the page gives nothing worth showing, so the card can stay
// empty without that counting as a failure.
export const getLinkPreview = async (
  url: string,
): Promise<LinkPreviewDto | null> =>
  (await request(`/link-preview?url=${encodeURIComponent(url)}`)).json()
